(() => {
  const PRODUCT_INFO_SELECTOR = 'product-info[data-conditional-color-pattern="true"]';
  const SHADE_PENDING_MESSAGE = 'Select a body shade: Light or Dark.';
  const LIGHT_DARK_VALUES = new Set(['light', 'dark']);

  const normalize = (value) => String(value || '').trim().toLowerCase();

  class ConditionalColorOptions {
    constructor(productInfo) {
      this.productInfo = productInfo;
      this.state = {
        primaryValue: '',
        shadeValue: '',
        secondaryValue: '',
        pendingShade: false,
      };

      this.handleChange = this.handleChange.bind(this);
      this.handleVariantUpdateComplete = this.handleVariantUpdateComplete.bind(this);

      this.productInfo.addEventListener('change', this.handleChange, true);
      this.productInfo.addEventListener('product-info:variant-update-complete', this.handleVariantUpdateComplete);
      this.initialize();
    }

    initialize() {
      this.captureCurrentState();

      const forcedShade = this.forcedShadeFor(this.state.primaryValue);
      if (forcedShade) {
        this.state.shadeValue = forcedShade;
        this.state.pendingShade = false;
      } else {
        this.state.pendingShade = Boolean(this.state.primaryValue) && !this.hasValidShade(this.state.shadeValue);
      }

      this.applyState();
    }

    get variantSelects() {
      return this.productInfo.querySelector('variant-selects');
    }

    get fixedShades() {
      return new Map(
        (this.productInfo.dataset.conditionalFixedShades || 'Black:Dark,White:Light')
          .split(',')
          .map((pair) => pair.split(':').map((value) => value.trim()))
          .filter(([color, shade]) => color && shade)
          .map(([color, shade]) => [normalize(color), shade])
      );
    }

    getRoleField(role) {
      return this.variantSelects?.querySelector(`[data-variant-role="${role}"]`);
    }

    getFieldValue(role) {
      const field = this.getRoleField(role);
      if (!field) return '';

      const checked = field.querySelector('input[type="radio"]:checked');
      if (checked) return checked.value;

      const select = field.querySelector('select');
      return select?.value || '';
    }

    setFieldValue(role, value) {
      const field = this.getRoleField(role);
      if (!field) return;

      const normalizedValue = normalize(value);
      const inputs = Array.from(field.querySelectorAll('input[type="radio"]'));
      if (inputs.length) {
        inputs.forEach((input) => {
          input.checked = normalizedValue !== '' && normalize(input.value) === normalizedValue;
        });
      }

      const select = field.querySelector('select');
      if (select) {
        const matchingOption = Array.from(select.options).find((option) => normalize(option.value) === normalizedValue);
        Array.from(select.options).forEach((option) => {
          const selected = option === matchingOption;
          option.selected = selected;
          option.toggleAttribute('selected', selected);
        });
        select.value = matchingOption ? matchingOption.value : '';
        if (!matchingOption) select.selectedIndex = -1;
      }
    }

    captureCurrentState() {
      const primaryValue = this.getFieldValue('primary_color');
      const shadeValue = this.getFieldValue('conditional_shade');
      const secondaryValue = this.getFieldValue('secondary_color');

      if (primaryValue) this.state.primaryValue = primaryValue;
      if (shadeValue) this.state.shadeValue = shadeValue;
      if (secondaryValue) this.state.secondaryValue = secondaryValue;
    }

    forcedShadeFor(value) {
      return this.fixedShades.get(normalize(value)) || '';
    }

    hasValidShade(value) {
      return LIGHT_DARK_VALUES.has(normalize(value));
    }

    handleChange(event) {
      const roleField = event.target.closest('[data-variant-role]');
      if (!roleField || !this.productInfo.contains(roleField)) return;

      const role = roleField.dataset.variantRole;
      const value = event.target.value;

      if (role === 'primary_color') {
        this.state.primaryValue = value;
        this.state.secondaryValue = this.getFieldValue('secondary_color');

        const forcedShade = this.forcedShadeFor(value);
        if (forcedShade) {
          this.state.shadeValue = forcedShade;
          this.state.pendingShade = false;
          this.setFieldValue('conditional_shade', forcedShade);
        } else {
          this.state.shadeValue = '';
          this.state.pendingShade = true;
          this.setFieldValue('conditional_shade', '');
        }
      } else if (role === 'conditional_shade') {
        this.state.shadeValue = value;
        this.state.pendingShade = !this.forcedShadeFor(this.state.primaryValue) && !this.hasValidShade(value);
      } else if (role === 'secondary_color') {
        this.state.secondaryValue = value;
      }

      this.applyState();
    }

    handleVariantUpdateComplete() {
      this.restoreSelections();
      this.applyState();

      if (this.state.pendingShade) {
        this.clearVariantId();
        this.productInfo.querySelector('product-form')?.toggleSubmitButton(true, window.variantStrings?.addToCart);
      }
    }

    restoreSelections() {
      if (this.state.primaryValue) this.setFieldValue('primary_color', this.state.primaryValue);
      if (this.state.secondaryValue) this.setFieldValue('secondary_color', this.state.secondaryValue);

      const forcedShade = this.forcedShadeFor(this.state.primaryValue);
      if (forcedShade) {
        this.setFieldValue('conditional_shade', forcedShade);
      } else if (this.state.pendingShade) {
        this.setFieldValue('conditional_shade', '');
      } else if (this.state.shadeValue) {
        this.setFieldValue('conditional_shade', this.state.shadeValue);
      }
    }

    clearVariantId() {
      this.productInfo.querySelectorAll('input[name="id"]').forEach((input) => {
        input.value = '';
        input.disabled = true;
      });
    }

    applyState() {
      const primaryValue = this.state.primaryValue || this.getFieldValue('primary_color');
      const forcedShade = this.forcedShadeFor(primaryValue);
      const shadeIsForced = Boolean(forcedShade);
      const shadeField = this.getRoleField('conditional_shade');
      const shadeStatus = shadeField?.querySelector('[data-conditional-shade-status]');
      const productForm = this.productInfo.querySelector('product-form');

      if (shadeIsForced) {
        this.state.shadeValue = forcedShade;
        this.state.pendingShade = false;
        this.setFieldValue('conditional_shade', forcedShade);
      } else if (primaryValue && !this.hasValidShade(this.state.shadeValue)) {
        this.state.pendingShade = true;
      }

      this.productInfo.dataset.conditionalShadePending = String(this.state.pendingShade);

      if (shadeField) {
        shadeField.hidden = false;
        shadeField.setAttribute('aria-disabled', String(shadeIsForced));

        shadeField.querySelectorAll('input[type="radio"], option').forEach((control) => {
          if (control.dataset.optionDisabled === 'true') {
            control.disabled = true;
          } else if (control.matches('input, option')) {
            control.disabled = shadeIsForced;
          }
        });
      }

      if (shadeStatus) {
        if (shadeIsForced) {
          shadeStatus.hidden = false;
          shadeStatus.textContent = `${primaryValue} uses ${forcedShade} shade.`;
        } else if (this.state.pendingShade) {
          shadeStatus.hidden = false;
          shadeStatus.textContent = SHADE_PENDING_MESSAGE;
        } else {
          shadeStatus.hidden = true;
          shadeStatus.textContent = '';
        }
      }

      this.updateDynamicCheckout(productForm);
    }

    updateDynamicCheckout(productForm) {
      const paymentButton = productForm?.querySelector('.shopify-payment-button');
      if (!paymentButton) return;

      paymentButton.hidden = this.state.pendingShade;
      paymentButton.setAttribute('aria-hidden', String(this.state.pendingShade));
      paymentButton.toggleAttribute('data-conditional-checkout-pending', this.state.pendingShade);
    }
  }

  const initialize = () => {
    document.querySelectorAll(PRODUCT_INFO_SELECTOR).forEach((productInfo) => {
      if (productInfo.conditionalColorOptions) return;
      productInfo.conditionalColorOptions = new ConditionalColorOptions(productInfo);
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
